package com.schoolconnect.app.feature.documents

import com.schoolconnect.app.datasource.network.response.DocumentResponseDTO
import com.schoolconnect.app.datasource.network.request.DocumentClassTargetRequest
import com.schoolconnect.app.datasource.network.request.DocumentUpdateRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.documents.DocumentLibraryRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.feature.common.DebouncePolicy
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.downloadFile
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.files.appendMimeTypeExtension
import com.schoolconnect.app.repository.files.mimeTypeForDocumentType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay

data class DocumentLibraryUiState(
    val documents: List<DocumentResponseDTO> = emptyList(),
    val totalCount: Int = 0,
    val isLoading: Boolean = false,
    val errorMessage: String = "",
    val query: String = "",
    val category: String = "",
    val applicableTo: String = "",
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val isSaving: Boolean = false
    ,val sortField: DocumentLibrarySortField = DocumentLibrarySortField.UPDATED_ON
    ,val sortAscending: Boolean = false
    ,val classOptions: List<DocumentLibraryClassOption> = emptyList()
    ,val isLoadingClassOptions: Boolean = false
    ,val classOptionsError: String = ""
    ,val metrics: com.schoolconnect.app.datasource.network.response.DocumentMetricsDTO? = null
    ,val categoryOptions: List<DocumentLibraryLookupOption> = emptyList()
    ,val audienceOptions: List<DocumentLibraryLookupOption> = emptyList()
    ,val isLoadingLookupOptions: Boolean = false
    ,val lookupOptionsError: String = ""
)

class DocumentLibraryViewModel(private val schoolId: String) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] DocumentLibraryViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var loadGeneration: Long = 0L
    private val _uiState = MutableStateFlow(DocumentLibraryUiState(isLoading = schoolId.isNotBlank()))
    val uiState: StateFlow<DocumentLibraryUiState> = _uiState.asStateFlow()

    init {
        if (schoolId.isBlank()) {
            _uiState.update { it.copy(isLoading = false, errorMessage = "School context was not available for document loading.") }
        } else {
            loadPage()
            loadMetrics()
            loadClassOptions()
            loadLookupOptions()
        }
    }

    fun onQueryChanged(value: String) {
        _uiState.update { it.copy(query = value, pageIndex = 0) }
        loadPage(debounce = true)
    }

    fun onCategoryChanged(value: String) {
        _uiState.update { it.copy(category = value, pageIndex = 0) }
        loadPage()
    }

    fun onApplicableToChanged(value: String) {
        _uiState.update { it.copy(applicableTo = value, pageIndex = 0) }
        loadPage()
    }

    fun clearFilters() {
        _uiState.update {
            it.copy(
                query = "",
                category = "",
                applicableTo = "",
                pageIndex = 0
            )
        }
        // Clear is a single server request. Keep the cleared state canonical
        // (blank optional filters) so later search, filter, and sort requests
        // use the same query/cache path as the initial load.
        loadPage(forceRemote = true)
    }

    fun onPageChanged(value: Int) {
        _uiState.update { it.copy(pageIndex = value.coerceAtLeast(0)) }
        loadPage()
    }

    fun onPageSizeChanged(value: Int) {
        _uiState.update { it.copy(pageSize = value.coerceAtLeast(1), pageIndex = 0) }
        loadPage()
    }

    fun onSortChanged(field: DocumentLibrarySortField) {
        _uiState.update { current ->
            current.copy(
                sortField = field,
                sortAscending = if (current.sortField == field) !current.sortAscending else true,
                pageIndex = 0
            )
        }
        loadPage()
    }

    fun refresh() = loadPage(forceRemote = true)

    fun loadDocumentDetails(documentId: String, onComplete: (DocumentLibraryRow) -> Unit) {
        scope.launch {
            DocumentLibraryRepository.loadDocument(documentId)
                .onSuccess { onComplete(it.toDocumentLibraryRow()) }
                .onFailure { throwable -> scLog("$TAG loadDocumentDetails failure document=$documentId", throwable) }
        }
    }

    fun download(row: DocumentLibraryRow) {
        scope.launch {
            DocumentLibraryRepository.getDocumentDownloadUrl(row.documentId.ifBlank { row.id })
                .mapCatching { url ->
                    val bytes = FileUploadRepository.downloadBytes(url).getOrThrow()
                    val metadata = row.fileId.takeIf { it.isNotBlank() }?.let { FileUploadRepository.getMetadata(it).getOrNull() }
                    val mimeType = metadata?.declaredMimeType?.trim()?.takeIf { it.isNotBlank() }
                        ?: metadata?.detectedMimeType?.trim()?.takeIf { it.isNotBlank() }
                        ?: mimeTypeForDocumentType(row.fileType)
                    val filename = appendMimeTypeExtension(
                        row.fileName.takeUnless { it == "—" }.orEmpty().ifBlank { "document-${row.id}" },
                        mimeType
                    )
                    check(downloadFile(filename, bytes, mimeType)) { "File downloads are not supported on this platform." }
                }
                .onFailure { throwable -> scLog("$TAG download failure document=${row.id}", throwable) }
        }
    }

    private fun loadMetrics() {
        scope.launch {
            DocumentLibraryRepository.loadMetrics().onSuccess { metrics ->
                _uiState.update { it.copy(metrics = metrics) }
            }.onFailure { throwable -> scLog("$TAG loadMetrics failure school=$schoolId", throwable) }
        }
    }

    fun createDocument(
        title: String,
        description: String,
        category: String,
        applicableTo: String,
        filename: String,
        contentType: String,
        bytes: ByteArray,
        classTargetIds: List<String>,
        onComplete: (String?) -> Unit
    ) {
        _uiState.update { it.copy(isSaving = true, errorMessage = "") }
        scope.launch {
            val state = _uiState.value
            val categoryCode = resolveLookupCode(category, state.categoryOptions)
            val audienceCode = resolveLookupCode(applicableTo, state.audienceOptions)
            if (categoryCode == null || audienceCode == null) {
                val message = "Document lookup values are unavailable. Please refresh and try again."
                _uiState.update { it.copy(isSaving = false, errorMessage = message) }
                onComplete(message)
                return@launch
            }
            DocumentLibraryRepository.create(
                schoolId = schoolId,
                title = title,
                description = description,
                category = categoryCode,
                applicableTo = audienceCode,
                classTargetIds = classTargetIds,
                filename = filename,
                contentType = contentType,
                bytes = bytes
            ).onSuccess {
                _uiState.update { it.copy(isSaving = false) }
                onComplete(null)
                loadPage(forceRemote = true)
                loadMetrics()
            }.onFailure { throwable ->
                val message = throwable.message ?: "Unable to add document."
                _uiState.update { it.copy(isSaving = false, errorMessage = message) }
                onComplete(message)
            }
        }
    }

    fun updateDocument(
        documentId: String,
        title: String,
        description: String,
        category: String,
        applicableTo: String,
        classTargetIds: List<String>,
        onComplete: (String?) -> Unit
    ) {
        _uiState.update { it.copy(isSaving = true, errorMessage = "") }
        scope.launch {
            val state = _uiState.value
            val categoryCode = resolveLookupCode(category, state.categoryOptions)
            val audienceCode = resolveLookupCode(applicableTo, state.audienceOptions)
            if (categoryCode == null || audienceCode == null) {
                val message = "Document lookup values are unavailable. Please refresh and try again."
                _uiState.update { it.copy(isSaving = false, errorMessage = message) }
                onComplete(message)
                return@launch
            }
            DocumentLibraryRepository.update(
                documentId,
                DocumentUpdateRequest(
                    title = title.trim(),
                    description = description.trim().ifBlank { null },
                    category = categoryCode,
                    applicableTo = audienceCode,
                    classTargets = classTargetIds.map { DocumentClassTargetRequest(it) }.takeIf { it.isNotEmpty() }
                )
            ).onSuccess {
                _uiState.update { it.copy(isSaving = false) }
                onComplete(null)
                loadPage(forceRemote = true)
                loadMetrics()
            }.onFailure { throwable ->
                val message = throwable.message ?: "Unable to update document."
                _uiState.update { it.copy(isSaving = false, errorMessage = message) }
                onComplete(message)
            }
        }
    }

    private fun loadClassOptions() {
        _uiState.update { it.copy(isLoadingClassOptions = true, classOptionsError = "") }
        scope.launch {
            ClassSectionRepository.loadSchoolClassSectionLookup(schoolId)
                .map { rows -> rows.mapNotNull { it.toDocumentLibraryClassOption() } }
                .onSuccess { options ->
                    val sortedOptions = options.sortedWith(
                        compareBy<DocumentLibraryClassOption> {
                            it.label.substringBefore('-').trim().toIntOrNull() ?: Int.MAX_VALUE
                        }.thenBy {
                            it.label.substringAfter('-', "").trim().lowercase()
                        }.thenBy { it.label.lowercase() }
                    )
                    _uiState.update { it.copy(classOptions = sortedOptions, isLoadingClassOptions = false) }
                }
                .onFailure { throwable ->
                    _uiState.update {
                        it.copy(isLoadingClassOptions = false, classOptionsError = throwable.message ?: "Unable to load classes.")
                    }
                }
        }
    }

    private fun loadLookupOptions() {
        _uiState.update { it.copy(isLoadingLookupOptions = true, lookupOptionsError = "") }
        scope.launch {
            SystemLookupRepository.refresh(forceRemote = false)
                .onSuccess { snapshot ->
                    fun options(category: String) = snapshot.activeValues(category).mapNotNull { item ->
                        val code = item.code?.trim().orEmpty()
                        val label = item.displayValue?.trim().takeUnless { it.isNullOrBlank() } ?: code
                        code.takeIf { it.isNotBlank() }?.let { DocumentLibraryLookupOption(it, label) }
                    }
                    val categories = options("DOCUMENT_CATEGORY")
                    val audiences = options("DOCUMENT_APPLICABLE_TO")
                    if (categories.isEmpty() || audiences.isEmpty()) {
                        _uiState.update { it.copy(isLoadingLookupOptions = false, lookupOptionsError = "Document lookup values are unavailable.") }
                    } else {
                        _uiState.update {
                            it.copy(
                                categoryOptions = categories,
                                audienceOptions = audiences,
                                isLoadingLookupOptions = false,
                                lookupOptionsError = ""
                            )
                        }
                    }
                }
                .onFailure { throwable ->
                    _uiState.update {
                        it.copy(isLoadingLookupOptions = false, lookupOptionsError = throwable.message ?: "Unable to load document lookup values.")
                    }
                }
        }
    }

    fun clear() {
        loadJob?.cancel()
        scope.cancel()
    }

    private fun loadPage(
        forceRemote: Boolean = false,
        debounce: Boolean = false,
        queryOverride: String? = null,
        categoryOverride: String? = null,
        audienceOverride: String? = null
    ) {
        val current = _uiState.value
        val generation = ++loadGeneration
        loadJob?.cancel()
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        loadJob = scope.launch {
            if (debounce) delay(DebouncePolicy.DEFAULT_DELAY_MS)
            val query = queryOverride ?: current.query
            val category = categoryOverride ?: current.category
            val applicableTo = audienceOverride ?: current.applicableTo
            val categoryCode = resolveFilterCode(category, "All Categories", current.categoryOptions)
            val audienceCode = resolveFilterCode(applicableTo, "All Users", current.audienceOptions)
            if ((category.isNotBlank() && categoryCode == null) || (applicableTo.isNotBlank() && audienceCode == null)) {
                _uiState.update { it.copy(isLoading = false, errorMessage = "Document lookup values are unavailable. Please refresh and try again.") }
                return@launch
            }
            DocumentLibraryRepository.loadPage(
                query = query,
                category = categoryCode,
                applicableTo = audienceCode,
                pageIndex = current.pageIndex,
                pageSize = current.pageSize,
                sortBy = current.sortField.apiField,
                order = if (current.sortAscending) ServerSortOrder.ASC.apiValue else ServerSortOrder.DESC.apiValue,
                forceRemote = forceRemote,
                refreshScope = scope,
            ).onSuccess { response ->
                if (generation != loadGeneration) return@onSuccess
                _uiState.update {
                    it.copy(
                        documents = response.items.orEmpty(),
                        totalCount = response.count ?: 0,
                        isLoading = false,
                        errorMessage = ""
                    )
                }
            }.onFailure { throwable ->
                if (generation != loadGeneration) return@onFailure
                scLog("$TAG loadPage failure school=$schoolId", throwable)
                _uiState.update { it.copy(isLoading = false, errorMessage = throwable.message ?: "Unable to load documents.") }
            }
        }
    }

    private fun resolveLookupCode(value: String, options: List<DocumentLibraryLookupOption>): String? =
        options.firstOrNull { option ->
            option.label.equals(value.trim(), ignoreCase = true) || option.code.equals(value.trim(), ignoreCase = true)
        }?.code

    private fun resolveFilterCode(value: String, allLabel: String, options: List<DocumentLibraryLookupOption>): String? =
        if (value.isBlank() || value.equals(allLabel, ignoreCase = true)) null else resolveLookupCode(value, options)
}
